import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { collection, doc, Firestore, onSnapshot, query, setDoc, where } from '@angular/fire/firestore';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { TenantService } from 'src/app/shared/services/tenant/tenant.service';
import { WhiteboardComponent } from '../../whiteboard/whiteboard.component';
import { CallControlsComponent } from '../call-controls/call-controls.component';
import { ChatBoxComponent } from '../chat-box/chat-box.component';
import { ChimeCallService } from '../chime-call.service';
import { ChimeService } from '../chime.service';
import { JoinCallSettingsComponent } from '../join-call-settings/join-call-settings.component';
import { ParticipantListComponent } from '../participant-list/participant-list.component';
import { VideoCallStateService } from '../video-call-state.service';
import { VideoTileComponent } from '../video-tile/video-tile.component';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';

@Component({
  selector: 'app-chime-meeting-page',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    TranslateModule,
    JoinCallSettingsComponent,
    CallControlsComponent,
    VideoTileComponent,
    ChatBoxComponent,
    MatIconModule,
    ParticipantListComponent,
    MatButtonModule,
    MatSnackBarModule,
    WhiteboardComponent,
  ],
  templateUrl: './chime-meeting-page.component.html',
  styleUrls: ['./chime-meeting-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Binds a class to the host element when in a meeting for background color
    '[class.in-meeting]': 'isMeetingActive()',
  },
})
export class ChimeMeetingPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private router = inject(Router);
  chimeService = inject(ChimeService);
  private chimeCallService = inject(ChimeCallService);
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private videoCallStateService = inject(VideoCallStateService);
  whiteboardOpened = this.videoCallStateService.whiteboardOpened;
  private breakpointObserverService = inject(BreakpointObserverService);
  isMobileOrTablet = computed(() => this.breakpointObserverService.isSmallScreen() || this.breakpointObserverService.isTabletScreen());

  constructor() {
    let wasActive = false;
    effect(() => {
      const active = this.isMeetingActive();
      if (active) {
        wasActive = true;
      } else if (wasActive) {
        wasActive = false;
        untracked(() => {
          this.waitingStatus.set('none');
        });
      }
    });

    effect(() => {
      const isHost = this.isHost();
      const callId = this.callID();
      const tenantId = this.tenantId();
      if (isHost && callId && tenantId) {
        this.setupHostLobbyListener(callId, tenantId);
      } else {
        this.cleanupHostLobbyListener();
      }
    });
  }

  // --- Component State ---
  callID = signal<string | null>(null);
  username = signal(this.authService.userInfo()?.fullName || '');
  email = signal('');
  urlOrSessionTenantId = signal('');
  private tenantService = inject(TenantService);
  tenantId = computed(() => this.urlOrSessionTenantId() || this.chimeService.tenantId() || this.tenantService.activeTenantId() || '');
  isGuest = computed(() => {
    const user = this.authService.userInfo();
    return !user?._id || !!user?.isAnonymous;
  });
  loading = signal(false);
  error = signal<string | null>(null);
  chatOpen = this.chimeService.isChatVisible;
  participantsOpen = signal(false);
  waitingStatus = signal<'none' | 'waiting' | 'admitted' | 'rejected'>('none');
  waitingParticipants = signal<any[]>([]);
  private lobbySubscription: any = null;
  private hostLobbySubscription: any = null;

  // --- Derived State from Service ---
  isMeetingActive = this.chimeService.isMeetingActive;
  participants = this.chimeService.participants;
  isHost = this.chimeService.isHost;

  localParticipant = this.chimeService.localParticipant;
  remoteParticipants = this.chimeService.remoteParticipants;

  allParticipants = this.chimeService.allParticipants;

  //Tracks if *any* main side panel is open (Chat or Participants)
  isSidePanelOpen = computed(() => this.chatOpen() || this.participantsOpen());

  localUserId = this.chimeService.localUserId;
  isScreenSharing = this.chimeService.isScreenSharing;
  screenShareTile = this.chimeService.screenShareTile;
  activeSpeakerId = this.chimeService.activeSpeakerId;
  isVideoFxActive = this.chimeService.isVideoFxActive;
  isRecording = this.chimeService.isRecording;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.callID.set(id);
    if (id) {
      this.chimeService.fetchMeetingSettings(id);

      // Restore session storage if available
      const cachedGuest = sessionStorage.getItem(`skz_guest_${id}`);
      if (cachedGuest) {
        try {
          const guestData = JSON.parse(cachedGuest);
          if (guestData.email) this.email.set(guestData.email);
          if (guestData.name) this.username.set(guestData.name);
          if (guestData.tenantId) this.urlOrSessionTenantId.set(guestData.tenantId);
        } catch (e) {
          console.error('Failed to parse cached guest session:', e);
        }
      }
    }

    const queryEmail = this.route.snapshot.queryParamMap.get('email');
    const queryName = this.route.snapshot.queryParamMap.get('name');
    const queryTenantId = this.route.snapshot.queryParamMap.get('tenantId');
    if (queryEmail) this.email.set(queryEmail);
    if (queryName) this.username.set(queryName);
    if (queryTenantId) this.urlOrSessionTenantId.set(queryTenantId);
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  async enterWaitingRoom(callId: string, username: string, email: string, userId: string): Promise<void> {
    this.waitingStatus.set('waiting');

    const guestDocRef = doc(this.firestore, 'workspace-events', this.tenantId(), 'contents', callId, 'lobby', userId);
    await setDoc(guestDocRef, {
      name: username,
      email: email || '',
      status: 'waiting',
      timestamp: new Date(),
    });

    if (this.lobbySubscription) {
      this.lobbySubscription();
    }

    this.lobbySubscription = onSnapshot(guestDocRef, async (snapshot) => {
      const data = snapshot.data();
      if (data?.status === 'admitted' && data.chimeAttendee) {
        this.cleanupLobbyListener();
        this.waitingStatus.set('admitted');
        await this.chimeService.joinMeetingWithAttendee(callId, username, data.chimeAttendee);
      } else if (data?.status === 'rejected') {
        this.cleanupLobbyListener();
        this.waitingStatus.set('rejected');
        this.error.set('VideoCall.Error.WaitingRoomRejected');
      }
    });
  }

  async onJoinCall(): Promise<void> {
    const callId = this.callID();
    if (this.loading() || !this.username().trim() || !callId) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      if (this.isGuest()) {
        const guestEmail = this.email().trim();
        if (!guestEmail) {
          this.error.set('Email address is required.');
          this.loading.set(false);
          return;
        }

        // Perform anonymous Firebase login if not already logged in
        if (!this.auth.currentUser) {
          await this.authService.loginAnonymously();
        }
        const firebaseUid = this.auth.currentUser?.uid;
        if (!firebaseUid) {
          throw new Error('Failed to obtain guest authentication ID.');
        }

        // Verify the guest against the backend invitation list
        const response: any = await firstValueFrom(this.chimeCallService.verifyGuest(callId, guestEmail, this.username().trim(), firebaseUid));
        if (response && response.tenantId) {
          this.urlOrSessionTenantId.set(response.tenantId);
        }

        // Save verified guest credentials to session storage
        sessionStorage.setItem(`skz_guest_${callId}`, JSON.stringify({ email: guestEmail, name: this.username().trim(), uid: firebaseUid, tenantId: this.tenantId() }));

        if (response?.waitingRoomRequired) {
          await this.enterWaitingRoom(callId, this.username().trim(), guestEmail, firebaseUid);
          return;
        }
      }

      // Fetch settings first to see if waiting room is active
      await this.chimeService.fetchMeetingSettings(callId);
      const isWaitingRoom = this.chimeService.isWaitingRoomEnabled();
      const isHost = this.isHost();

      if (isWaitingRoom && !isHost) {
        const userId = this.authService.getID() || this.auth.currentUser?.uid || 'anonymous-guest';
        const username = this.username().trim();
        const email = this.email()?.trim() || this.authService.userInfo()?.email || '';
        await this.enterWaitingRoom(callId, username, email, userId);
      } else {
        await this.chimeService.joinMeeting(callId, this.username());
      }
    } catch (err: any) {
      console.error(err);
      let msg = err.error?.message || err.message || 'Failed to join the call. Please try again.';
      if (msg.includes('You are not on the invitation list') || msg.includes('Access denied: You are not on the invitation list.')) {
        const userId = this.authService.getID() || this.auth.currentUser?.uid || 'anonymous-guest';
        const username = this.username().trim();
        const email = this.email()?.trim() || this.authService.userInfo()?.email || '';
        try {
          await this.enterWaitingRoom(callId, username, email, userId);
        } catch (lobbyErr) {
          console.error('Failed to enter waiting room after access denied:', lobbyErr);
          this.error.set('VideoCall.Error.AccessDenied');
        }
      } else if (msg.includes('Missing or insufficient permissions') || err.code === 'permission-denied') {
        msg = 'VideoCall.Error.PermissionDenied';
        this.error.set(msg);
      } else {
        this.error.set(msg);
      }
    } finally {
      this.loading.set(false);
    }
  }

  onLeaveCall(): void {
    const callId = this.callID();
    if (this.isHost() && callId) {
      this.chimeCallService.endCallAndMeeting(callId).subscribe({
        next: () => {
          console.log('Call and meeting ended successfully.');
          this.chimeService.leaveMeeting();
          this.chatOpen.set(false);
          this.participantsOpen.set(false);
        },
        error: (err) => {
          console.error('Failed to end call and meeting:', err);
          this.chimeService.leaveMeeting();
          this.chatOpen.set(false);
          this.participantsOpen.set(false);
        },
      });
    } else {
      this.chimeService.leaveMeeting();
      this.chatOpen.set(false);
      this.participantsOpen.set(false);
    }
  }

  onMuteParticipant(attendeeId: string): void {
    this.chimeService.muteParticipant(attendeeId);
  }

  onToggleScreenShare(mode?: 'content-only' | 'with-sidebar' | 'full'): void {
    if (this.isScreenSharing()) {
      this.chimeService.stopScreenShare();
    } else {
      this.chimeService.startScreenShare(mode || 'content-only');
    }
  }

  onToggleVideoFx(): void {
    this.chimeService.toggleBackgroundBlur();
  }

  onToggleChat(): void {
    const isOpen = this.chatOpen();
    this.chatOpen.set(!isOpen);
    this.participantsOpen.set(false); // Close other panel
  }

  onToggleParticipants(): void {
    const isOpen = this.participantsOpen();
    this.participantsOpen.set(!isOpen);
    this.chatOpen.set(false); // Close other panel
  }

  async onLeaveWaitingRoom() {
    const callId = this.callID();
    const userId = this.authService.getID() || this.auth.currentUser?.uid;
    if (callId && userId) {
      try {
        const guestDocRef = doc(this.firestore, 'workspace-events', this.tenantId(), 'contents', callId, 'lobby', userId);
        await setDoc(guestDocRef, { status: 'left' }, { merge: true });
      } catch (e) {
        console.error('Failed to update waiting room status on leave:', e);
      }
    }
    this.cleanupLobbyListener();
    this.waitingStatus.set('none');
  }

  private setupHostLobbyListener(callId: string, tenantId: string) {
    this.cleanupHostLobbyListener();
    let isInitialSnapshot = true;
    const lobbyCollRef = collection(this.firestore, 'workspace-events', tenantId, 'contents', callId, 'lobby');
    const q = query(lobbyCollRef, where('status', '==', 'waiting'));
    this.hostLobbySubscription = onSnapshot(q, (snapshot) => {
      const newWaiting = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as any);

      if (!isInitialSnapshot) {
        const hasNewWaiters = snapshot.docChanges().some(change => change.type === 'added');
        if (hasNewWaiters) {
          this.chimeService.playChimeSound();
        }
      }
      isInitialSnapshot = false;

      this.waitingParticipants.set(newWaiting);
    });
  }

  private cleanupHostLobbyListener() {
    if (this.hostLobbySubscription) {
      this.hostLobbySubscription();
      this.hostLobbySubscription = null;
    }
    this.waitingParticipants.set([]);
  }

  private cleanupLobbyListener() {
    if (this.lobbySubscription) {
      this.lobbySubscription();
      this.lobbySubscription = null;
    }
  }

  async onAdmitParticipant(guestUserId: string) {
    const callId = this.callID();
    if (!callId) return;
    const guest = this.waitingParticipants().find((w) => w.id === guestUserId);
    if (!guest) return;
    try {
      await firstValueFrom(this.chimeCallService.admitParticipant(callId, guestUserId, guest.name, guest.email));
    } catch (err) {
      console.error('Failed to admit participant:', err);
    }
  }

  async onRejectParticipant(guestUserId: string) {
    const callId = this.callID();
    if (!callId) return;
    try {
      await firstValueFrom(this.chimeCallService.rejectParticipant(callId, guestUserId));
    } catch (err) {
      console.error('Failed to reject participant:', err);
    }
  }

  ngOnDestroy(): void {
    if (this.chimeService.videoPreviewIsActive()) {
      this.chimeService.stopVideoPreview();
    }
    this.cleanupLobbyListener();
    this.cleanupHostLobbyListener();
    this.chimeService.leaveMeeting();
  }
}
